import React, { useEffect, useMemo, useState, useRef } from "react";
import { Form, SimpleItem } from "devextreme-react/ui/form";
import { getStorageMeta } from "../../../api";
import CollapseSection from "../../../components/CollaspeSection";
import { getDateFormat } from "../../../utils/date-format";
import { useQuery } from "@tanstack/react-query";
import {
  applyTimeIntervalSelection,
  cloneReportInfoForForm,
  getTimeIntervalVisibility,
} from "./report-params-utils";
import {
  ALL_SELECTED_FILTERS_KEY,
  UNASSIGNED_FILTER_VALUE,
  displayWithUnassigned,
  unassignedOption,
} from "./filter-constants";

const TIME_DIMENSION_ITEMS = ["Date Created", "Date Assigned", "Date Released"];

const StorageParamsForm = ({ paramsForm, reportInfo }) => {
  const [editorVisiblity, setEditorVisbility] = useState(
    getTimeIntervalVisibility(reportInfo?.report_parameters?.time_interval)
  );

  let startDateRef = null;
  let endDateRef = null;

  const {
    data: {
      types: storageTypes,
      sizes: storageSizes,
      brands: storageBrands,
      model_names: storageModels,
    },
  } = useQuery({
    queryKey: ["storage:meta"],
    queryFn: () => getStorageMeta(),
    initialData: {
      storage_types: [],
      storage_sizes: [],
      storage_brands: [],
      storage_models: [],
    },
    refetchOnWindowFocus: false,
  });

  // Option lists are memoized so the seeding effects below can set tag box
  // values derived from the same arrays the editors' items use. The initial
  // query data has no types/sizes/brands/model_names keys, so each list
  // falls back to an empty array until the lookup resolves.
  const storageTypeOptions = useMemo(
    () => [unassignedOption("type"), ...(storageTypes || [])],
    [storageTypes]
  );

  // Zero capacity entries (like "0 GB") are excluded from the dropdown: the
  // unassigned sentinel now represents storage with no size provided.
  const storageSizeOptions = useMemo(
    () => [
      unassignedOption("storage_size"),
      ...(storageSizes || []).filter((s) => parseFloat(s.storage_size) !== 0),
    ],
    [storageSizes]
  );

  const storageBrandOptions = useMemo(
    () => [unassignedOption("make"), ...(storageBrands || [])],
    [storageBrands]
  );

  const storageModelOptions = useMemo(
    () => [unassignedOption("model_name"), ...(storageModels || [])],
    [storageModels]
  );

  // Fields saved as "all selected" reseed their tag box fully checked once
  // the lookup resolves. Values are derived from the current options, not a
  // snapshot, so options added since the save show up checked too.
  const allSelectedFilters = useMemo(
    () => reportInfo?.report_parameters?.[ALL_SELECTED_FILTERS_KEY] || [],
    [reportInfo]
  );

  const seededRef = useRef({});
  const seedAllSelected = (paramKey, values) => {
    if (seededRef.current[paramKey]) return;
    if (!allSelectedFilters.includes(paramKey)) return;
    // A sentinel bearing field carries its "__unassigned__" option
    // synchronously, before the lookup resolves, so wait for at least one real
    // (non sentinel) value before seeding. Seeding and locking on the sentinel
    // only state would strand the field on "(No ...)".
    const hasRealValue = values.some((v) =>
      v && typeof v === "object"
        ? !Object.values(v).includes(UNASSIGNED_FILTER_VALUE)
        : v !== UNASSIGNED_FILTER_VALUE
    );
    if (!hasRealValue) return;
    // Seed each all selected field once. Re-running on later option identity
    // changes (e.g. a shared query refetch) would reset the tag box to fully
    // checked and clobber a partial deselection the user made mid edit.
    seededRef.current[paramKey] = true;
    paramsForm.current?.instance?.updateData(
      `report_parameters.${paramKey}`,
      values
    );
  };

  useEffect(() => {
    seedAllSelected(
      "storage_types",
      storageTypeOptions.map((o) => o.type)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageTypeOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "storage_sizes",
      storageSizeOptions.map((o) => o.storage_size)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageSizeOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "storage_brands",
      storageBrandOptions.map((o) => o.make)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageBrandOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "storage_models",
      storageModelOptions.map((o) => o.model_name)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageModelOptions, allSelectedFilters]);

  return (
    <>
      <CollapseSection
        title={"Report Parameters | Storage - Overview"}
        allowCollapse={false}
      >
        <Form
          ref={paramsForm}
          colCount={2}
          defaultFormData={cloneReportInfoForForm(reportInfo)}
        >
          <SimpleItem
            dataField="report_parameters.time_interval"
            label={{ text: "Time Interval", location: "top" }}
            colSpan={1}
            editorType="dxSelectBox"
            editorOptions={{
              items: [
                "All Time",
                "Custom",
                "Today",
                "Yesterday",
                "This Week",
                "Last Week",
                "This Month",
                "Last Month",
                "This Year",
                "Last Year",
              ],
              placeholder: "All Time",
              dropDownOptions: {
                maxHeight: 250,
              },
              onValueChanged: (e) => {
                const interval = applyTimeIntervalSelection(
                  e,
                  paramsForm.current?.instance,
                  TIME_DIMENSION_ITEMS
                );
                setEditorVisbility({
                  ...editorVisiblity,
                  ...getTimeIntervalVisibility(interval),
                });
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.timestamp_dimension"
            label={{ text: "Time Dimension", location: "top" }}
            colSpan={1}
            editorType="dxSelectBox"
            isRequired={true}
            visible={editorVisiblity.dimension}
            editorOptions={{
              items: TIME_DIMENSION_ITEMS,
            }}
          />
          <SimpleItem
            dataField="report_parameters.start_date"
            label={{ text: "Start Date", location: "top" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.startDate}
            editorOptions={{
              onInitialized: (e) => {
                startDateRef = e.component;
              },
              onValueChanged: (e) => {
                endDateRef.option("min", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />
          <SimpleItem
            dataField="report_parameters.end_date"
            label={{ text: "End Date", location: "top" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.endDate}
            editorOptions={{
              onInitialized: (e) => {
                endDateRef = e.component;
              },
              onValueChanged: (e) => {
                startDateRef.option("max", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />
          <SimpleItem
            dataField="report_parameters.storage_types"
            label={{ text: "Types" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "type",
              displayExpr: displayWithUnassigned("type", "(No Storage Type)"),
              items: storageTypeOptions,
              placeholder: "All Storage Types",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.storage_sizes"
            label={{ text: "Sizes" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "storage_size",
              displayExpr: displayWithUnassigned(
                "storage_size",
                "(No Size Provided)"
              ),
              items: storageSizeOptions,
              placeholder: "All Storage Sizes",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.storage_brands"
            label={{ text: "Brands" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "make",
              displayExpr: displayWithUnassigned("make", "(No Brand)"),
              items: storageBrandOptions,
              placeholder: "All Storage Brands",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.storage_models"
            label={{ text: "Model Names" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "model_name",
              displayExpr: displayWithUnassigned("model_name", "(No Model)"),
              items: storageModelOptions,
              placeholder: "All Storage Models",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
        </Form>
      </CollapseSection>
    </>
  );
};

export default StorageParamsForm;
